import { useAutoneTranslation } from '@autone/translations';
import {
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellDate,
  DataTableCellTitle,
  DataTableHeaderCellWithSorting,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';

import { type APP_NAME } from '../../../consts';
import { useAssortmentTableContext } from '../contexts';

import { type CommonAssortmentTableItem } from './common-columns.types';

const COLUMN_ID = 'latest_sale_date' as const;

export type LatestSaleDateColumnId = typeof COLUMN_ID;

interface LatestSaleDateFields {
  latest_sale_date: CommonAssortmentTableItem['latest_sale_date'];
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<LatestSaleDateFields>();

export const createLatestSaleDateColumn = <
  T extends LatestSaleDateFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.latest_sale_date),
    {
      id: COLUMN_ID,
      size: 175,
      header: function LatestSaleDateHeader({ column }) {
        const { t } = useAutoneTranslation<typeof APP_NAME>();
        const tableName = useAssortmentTableContext();

        const latestSaleDateHeaderCellContents = (
          <DataTableCellContent align={'right'}>
            <DataTableCellTitle className="uppercase font-medium whitespace-normal text-end">
              {t('table-columns.latest-sale-date')}
            </DataTableCellTitle>
          </DataTableCellContent>
        );

        if (
          tableName === 'productsTable' ||
          tableName === 'productLocationsTable'
        ) {
          return (
            <DataTableHeaderCellWithSorting
              column={column}
              arrowPosition="left"
              showSortable
            >
              {latestSaleDateHeaderCellContents}
            </DataTableHeaderCellWithSorting>
          );
        }

        return latestSaleDateHeaderCellContents;
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function LatestSaleDateCell({ getValue }) {
            return (
              <DataTableCellContent align="right">
                <DataTableCellTitle>
                  <DataTableCellDate value={getValue()} />
                </DataTableCellTitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
